import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '@zag/ui';
import { type PreferenceResponse } from '@zag/notifications/common';
import { NotificationsService } from '../../services/notifications.service';

const EVENT_TYPES = [
  'shift.assigned',
  'shift.unassigned',
  'shift.updated',
  'plan.published',
  'plan.updated',
  'availability.requested',
  'availability.approved',
  'availability.rejected',
  'shift_time_override_by_employee',
  'shift_override_reset_by_planner',
];

/**
 * User in-app notification preferences.
 *
 * Shows a simple list of event types with on/off toggles for in-app
 * notifications. In-app notifications are always available at the user
 * level and default to enabled.
 *
 * Other channels (webhook, email, etc.) are configured in their
 * respective dedicated management pages, not here.
 */
@Component({
  selector: 'zag-notification-preferences',
  imports: [TranslatePipe, ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notification-preferences.component.html',
  styleUrls: ['./notification-preferences.component.scss'],
})
export class NotificationPreferencesComponent {
  private readonly notificationsService = inject(NotificationsService);

  /** When true, the component renders in read-only mode regardless of permissions. */
  readonly forceReadonly = input<boolean>(false);

  /**
   * Whether the current user has permission to manage notification preferences.
   * Pass `permissionService.hasPermission('notifications:manage')` from the parent.
   */
  readonly hasManagePermission = input<boolean>(false);

  /** Whether the user can manage notification preferences */
  readonly canManage = computed(() => this.hasManagePermission() && !this.forceReadonly());

  readonly isDirty = signal(false);

  /** Emits the full preferences map whenever the user toggles a preference. */
  readonly preferencesChanged = output<Record<string, boolean>>();

  /** Local writable preferences map (used when the user makes edits) */
  private readonly localPrefs = signal<Record<string, boolean>>({});

  readonly eventTypes = EVENT_TYPES;

  /** rxResource-based preferences loading (in_app channel only) */
  private readonly preferencesResource = rxResource<PreferenceResponse[], undefined>({
    stream: () => this.notificationsService.getPreferences(),
  });

  readonly isLoading = computed(() => this.preferencesResource.isLoading());
  readonly hasError = computed(() => this.preferencesResource.status() === 'error');

  /** Computed preferences from server data: eventType -> enabled (in_app only) */
  private readonly serverPrefs = computed((): Record<string, boolean> => {
    const preferences = this.preferencesResource.value();
    if (!preferences) return {};

    const prefs: Record<string, boolean> = {};
    for (const eventType of this.eventTypes) {
      const pref = preferences.find((p: PreferenceResponse) => p.eventType === eventType && p.channel === 'in_app');
      // Default: in_app always enabled unless explicitly disabled
      prefs[eventType] = pref ? pref.enabled : true;
    }
    return prefs;
  });

  /** The preferences to render - uses local dirty state if user made changes, else server state */
  readonly prefs = computed(() => {
    if (this.isDirty()) {
      return this.localPrefs();
    }
    return this.serverPrefs();
  });

  isEnabled(eventType: string): boolean {
    return this.prefs()[eventType] ?? true;
  }

  togglePreference(eventType: string): void {
    if (!this.canManage()) return;

    // Initialize local prefs from server state on first edit
    if (!this.isDirty()) {
      this.localPrefs.set({ ...this.serverPrefs() });
      this.isDirty.set(true);
    }

    this.localPrefs.update(p => ({
      ...p,
      [eventType]: !(p[eventType] ?? true),
    }));
    this.preferencesChanged.emit(this.localPrefs());
  }

  /** Resets dirty state and reloads preferences from server. Called by parent after save. */
  resetDirtyState(): void {
    this.isDirty.set(false);
    this.localPrefs.set({});
    this.preferencesResource.reload();
  }

  retryLoad(): void {
    this.preferencesResource.reload();
  }
}
